import React from "react";
import { ChevronDown, ChevronUp, Mail, Smartphone } from "lucide-react";
import { formatNotificationDate } from "@/utils/timeFormatUtils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { NotificationLog, SortDirection, SortField } from "../types";
import { NotificationStatusBadge } from "./NotificationStatusBadge";
import { NotificationLogActions } from "./NotificationLogActions";
import { Badge } from "@/components/ui/badge";

interface NotificationLogListProps {
  logs: NotificationLog[];
  totalPages: number;
  currentPage: number;
  totalElements: number;
  pageSize: number;
  isLoading: boolean;
  onResend: (id: string) => void;
  onCancel?: (id: string) => void;
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  onPageChange: (page: number) => void;
}

export function NotificationLogList({
  logs,
  totalPages,
  currentPage,
  totalElements,
  pageSize,
  isLoading,
  onResend,
  onCancel,
  sortField,
  sortDirection,
  onSort,
  onPageChange,
}: NotificationLogListProps) {
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? (
      <ChevronUp className="h-4 w-4 ml-1" />
    ) : (
      <ChevronDown className="h-4 w-4 ml-1" />
    );
  };

  const getSortableHeaderProps = (field: SortField) => ({
    onClick: () => onSort(field),
    className: "cursor-pointer hover:bg-muted/50",
  });

  const renderChannelIcon = (channel: "EMAIL" | "SMS") => {
    return channel === "EMAIL" ? (
      <Mail className="h-4 w-4 mr-2" />
    ) : (
      <Smartphone className="h-4 w-4 mr-2" />
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (logs.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground h-64 flex items-center justify-center">
            No notification logs found.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-6">
        {/* Table */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead {...getSortableHeaderProps("templateName")}>
                  <div className="flex items-center">
                    Template Type
                    {renderSortIcon("templateName")}
                  </div>
                </TableHead>
                <TableHead {...getSortableHeaderProps("recipient")}>
                  <div className="flex items-center">
                    Recipient
                    {renderSortIcon("recipient")}
                  </div>
                </TableHead>
                <TableHead {...getSortableHeaderProps("channel")}>
                  <div className="flex items-center">
                    Channel
                    {renderSortIcon("channel")}
                  </div>
                </TableHead>
                <TableHead {...getSortableHeaderProps("status")}>
                  <div className="flex items-center">
                    Status
                    {renderSortIcon("status")}
                  </div>
                </TableHead>
                <TableHead {...getSortableHeaderProps("scheduledTime")}>
                  <div className="flex items-center">
                    Date
                    {renderSortIcon("scheduledTime")}
                  </div>
                </TableHead>
                <TableHead className="w-[200px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="font-medium">
                    {log.templateName}
                  </TableCell>
                  <TableCell>{log.recipient}</TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      {renderChannelIcon(log.channel)}
                      <Badge variant="secondary">
                        {log.channel.charAt(0).toUpperCase() +
                          log.channel.slice(1)}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <NotificationStatusBadge status={log.status} />
                  </TableCell>
                  <TableCell>
                    {formatNotificationDate(log.scheduledTime)}
                  </TableCell>
                  <TableCell>
                    <NotificationLogActions
                      log={log}
                      onResend={onResend}
                      onCancel={onCancel}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination (following AssignmentTable pattern) */}
        {totalPages > 1 && (
          <div className="mt-4">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage > 0) onPageChange(currentPage - 1);
                    }}
                    aria-disabled={currentPage === 0}
                  />
                </PaginationItem>
                {/* Truncated Pagination Logic */}
                {(() => {
                  if (totalPages <= 7) {
                    // Show all pages
                    return Array.from({ length: totalPages }).map((_, idx) => (
                      <PaginationItem key={idx}>
                        <PaginationLink
                          href="#"
                          isActive={currentPage === idx}
                          onClick={(e) => {
                            e.preventDefault();
                            onPageChange(idx);
                          }}
                        >
                          {idx + 1}
                        </PaginationLink>
                      </PaginationItem>
                    ));
                  }
                  // Truncated windowed pagination - using 0-indexed logic throughout
                  const pageLinks = [];
                  const window = 2;
                  // Calculate 0-indexed start and end positions
                  const start = Math.max(1, currentPage - window);
                  const end = Math.min(totalPages - 2, currentPage + window);

                  // Always show first page (0-indexed)
                  pageLinks.push(
                    <PaginationItem key={0}>
                      <PaginationLink
                        href="#"
                        isActive={currentPage === 0}
                        onClick={(e) => {
                          e.preventDefault();
                          onPageChange(0);
                        }}
                      >
                        1
                      </PaginationLink>
                    </PaginationItem>,
                  );

                  // Ellipsis after first page
                  if (start > 1) {
                    pageLinks.push(
                      <PaginationItem key="start-ellipsis">
                        <span className="px-2">...</span>
                      </PaginationItem>,
                    );
                  }

                  // Pages in window (0-indexed loop, 1-indexed display)
                  for (let i = start; i <= end; i++) {
                    pageLinks.push(
                      <PaginationItem key={i}>
                        <PaginationLink
                          href="#"
                          isActive={currentPage === i}
                          onClick={(e) => {
                            e.preventDefault();
                            onPageChange(i);
                          }}
                        >
                          {i + 1}
                        </PaginationLink>
                      </PaginationItem>,
                    );
                  }

                  // Ellipsis before last page
                  if (end < totalPages - 2) {
                    pageLinks.push(
                      <PaginationItem key="end-ellipsis">
                        <span className="px-2">...</span>
                      </PaginationItem>,
                    );
                  }

                  // Always show last page (0-indexed)
                  if (totalPages > 1) {
                    pageLinks.push(
                      <PaginationItem key={totalPages - 1}>
                        <PaginationLink
                          href="#"
                          isActive={currentPage === totalPages - 1}
                          onClick={(e) => {
                            e.preventDefault();
                            onPageChange(totalPages - 1);
                          }}
                        >
                          {totalPages}
                        </PaginationLink>
                      </PaginationItem>,
                    );
                  }

                  return pageLinks;
                })()}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage < totalPages - 1)
                        onPageChange(currentPage + 1);
                    }}
                    aria-disabled={currentPage === totalPages - 1}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}

        {/* Total elements info */}
        {totalElements > 0 && (
          <div className="mt-4 text-sm text-muted-foreground text-center">
            Showing {currentPage * pageSize + 1} to{" "}
            {Math.min((currentPage + 1) * pageSize, totalElements)} of{" "}
            {totalElements} results
          </div>
        )}
      </CardContent>
    </Card>
  );
}
